/**
 * UnlockSuccess — post-purchase landing.
 *
 * Stripe redirects here with `?session_id=...` after a successful $2.99
 * checkout. This page:
 *   1. Verifies payment by polling `/entitlements/me` (webhook may still
 *      be in flight when Stripe redirects).
 *   2. Shows the 3-skin starter picker so the pilot claims their bundle.
 *   3. Redirects to /levels after claim.
 */
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, Check, Rocket } from "lucide-react";
import Navbar from "../components/Navbar";
import api from "../lib/api";
import useGameAccess from "../lib/useGameAccess";

function SkinPreview({ item, picked, disabled, onToggle }) {
  const colors = item.colors && item.colors.length ? item.colors : ["#888"];
  const bg = colors.length >= 2
    ? `linear-gradient(135deg, ${colors[0]} 0%, ${colors[1] || colors[0]} 100%)`
    : colors[0];
  return (
    <button
      type="button"
      onClick={() => !disabled && onToggle(item.id)}
      disabled={disabled}
      className={`relative border-2 p-4 text-left transition ${
        picked
          ? "border-[#ffd700] bg-[#ffd700]/[0.08]"
          : disabled
            ? "border-white/10 opacity-40 cursor-not-allowed"
            : "border-white/20 hover:border-white/50"
      }`}
      data-testid={`starter-skin-${item.id}`}
    >
      <div
        className="w-16 h-16 rounded-full mx-auto mb-3 border border-white/20"
        style={{
          background: bg,
          boxShadow: `0 0 24px ${colors[0]}55`,
        }}
      />
      <div className="text-center font-mono-cosmic text-[10px] tracking-[0.25em] uppercase text-white/85">
        {item.name}
      </div>
      {picked && (
        <div className="absolute top-2 right-2 w-5 h-5 bg-[#ffd700] flex items-center justify-center">
          <Check className="w-3 h-3 text-black" />
        </div>
      )}
    </button>
  );
}

export default function UnlockSuccess() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { snapshot, refresh } = useGameAccess();
  const [catalog, setCatalog] = useState([]);
  const [picks, setPicks] = useState([]);
  const [busy, setBusy] = useState(false);
  const [poll, setPoll] = useState(0);

  // Poll entitlements/me every 1.5s until access === "paid". If we have
  // a Stripe session_id in the URL, ALSO hit /verify-session — that's
  // the webhook-independent fallback that guarantees the purchase
  // applies even if the Stripe webhook isn't wired up yet.
  useEffect(() => {
    if (snapshot?.access === "paid") return;
    const id = setInterval(async () => {
      if (sessionId) {
        try {
          await api.get(`/entitlements/verify-session/${sessionId}`);
        } catch { /* ignore transient */ }
      }
      refresh();
      setPoll((p) => p + 1);
    }, 1500);
    return () => clearInterval(id);
  }, [snapshot?.access, refresh, sessionId]);

  useEffect(() => {
    if (snapshot?.access === "paid" && snapshot?.needs_starter_skins) {
      api.get("/entitlements/starter-catalog")
        .then((r) => setCatalog(r.data?.items || []))
        .catch(() => {});
    } else if (snapshot?.access === "paid" && !snapshot?.needs_starter_skins) {
      // Already claimed — bounce to /levels.
      navigate("/levels", { replace: true });
    }
  }, [snapshot?.access, snapshot?.needs_starter_skins, navigate]);

  const togglePick = (id) => {
    setPicks((cur) => {
      if (cur.includes(id)) return cur.filter((x) => x !== id);
      if (cur.length >= 3) return cur;
      return [...cur, id];
    });
  };

  const submitPicks = async () => {
    if (picks.length !== 3) {
      toast.error("Pick exactly 3 starter skins");
      return;
    }
    setBusy(true);
    try {
      await api.post("/entitlements/claim-starter", { skin_ids: picks });
      toast.success("Starter skins claimed! Equip them in /skins.");
      navigate("/levels", { replace: true });
    } catch (e) {
      toast.error("Claim failed", { description: e?.response?.data?.detail || e.message });
      setBusy(false);
    }
  };

  const stillWaiting = snapshot?.access !== "paid";

  return (
    <div className="min-h-screen bg-[#05050a] text-white">
      <Navbar />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
        {stillWaiting ? (
          <>
            <div className="font-mono-cosmic text-[10px] tracking-[0.3em] uppercase text-[#00ffff] mb-2">
              // CONFIRMING · PAYMENT
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl mb-4">
              Verifying your purchase…
            </h1>
            <div className="text-white/70 text-sm">
              Stripe is confirming with our server. This usually takes 2–5 seconds
              (retry {poll}).
            </div>
          </>
        ) : (
          <>
            <div className="flex items-center gap-2 mb-2">
              <Rocket className="w-4 h-4 text-[#39ff14]" />
              <div className="font-mono-cosmic text-[10px] tracking-[0.3em] uppercase text-[#39ff14]">
                // PURCHASE · CONFIRMED
              </div>
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl mb-1">
              Welcome, Pilot.
            </h1>
            <div className="text-white/70 text-sm mb-6">
              You&apos;ve unlocked lifetime access. Pick your 3 free starter ball skins below.
            </div>

            {/* Founder badge — only shown when the pilot claimed a slot. */}
            {snapshot?.founder_number && (
              <div
                className="mb-6 border-2 border-[#ffd700]/70 bg-gradient-to-r from-[#ffd700]/[0.15] via-[#b026ff]/[0.10] to-[#ffd700]/[0.15] p-4 flex items-center gap-3"
                data-testid="founder-badge"
              >
                <Sparkles className="w-6 h-6 text-[#ffd700] flex-shrink-0" />
                <div>
                  <div className="font-heading uppercase tracking-tight text-lg text-[#ffd700]">
                    Founder #{snapshot.founder_number}
                  </div>
                  <div className="font-mono-cosmic text-[10px] tracking-[0.25em] uppercase text-white/70 mt-0.5">
                    Founder&apos;s Edition ball skin locked to your account · equip via /skins
                  </div>
                </div>
              </div>
            )}

            <div className="mb-6 flex items-center justify-between flex-wrap gap-2">
              <div className="font-mono-cosmic text-[10px] tracking-[0.3em] uppercase text-white/55 flex items-center gap-2">
                <Sparkles className="w-3.5 h-3.5 text-[#ffd700]" />
                Choose 3 · picked {picks.length}/3
              </div>
              <button
                type="button"
                onClick={submitPicks}
                disabled={picks.length !== 3 || busy}
                className="px-4 py-2 bg-[#ffd700] hover:bg-[#ffed7a] text-black font-heading uppercase tracking-tight text-sm transition disabled:opacity-40"
                data-testid="starter-claim-btn"
              >
                {busy ? "Claiming…" : "Claim & Play"}
              </button>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {catalog.map((item) => {
                const picked = picks.includes(item.id);
                const disabled = !picked && picks.length >= 3;
                return (
                  <SkinPreview
                    key={item.id}
                    item={item}
                    picked={picked}
                    disabled={disabled}
                    onToggle={togglePick}
                  />
                );
              })}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
